import { IonContent, IonPage } from "@ionic/react";
import type { ReactNode } from "react";

import { Notice } from "../../components/Notice";
import { Screen } from "../../components/Shell";
import { TopBar } from "../../components/TopBar";
import type { Linked, UseLinked } from "../../lib/linked";
import { useKaya } from "../../store/kayaStore";
import "../You.css";

type Back = { to: string; label: string };

/**
 * What a page about a linked person (ADR-046) shows while their state is not in hand: the reason
 * the server refused — the link was removed, or is still waiting — or nothing while it loads.
 * Content only: the route keeps one `IonPage` whatever is inside it, because a route that swaps
 * which `IonPage` it returns leaves the outlet holding a page it no longer tracks (see
 * admin/UserViewNotice.tsx).
 */
export function LinkedWaiting({ q, title, back }: { q: UseLinked; title: string; back: Back }) {
  return (
    <IonContent>
      <Screen>
        <TopBar title={title} back={back} />
        <div className="you-page">
          {q.error ? (
            <Notice tone="danger" primary={{ label: "Try again", onClick: () => void q.reload() }}>
              {q.error === "No such link"
                ? "This link has been removed, or is still waiting to be accepted."
                : `${q.error}. Their plan is read from Kaya's server, so it needs a connection.`}
            </Notice>
          ) : null}
        </div>
      </Screen>
    </IonContent>
  );
}

/** The frame of a page about a linked person: their page once loaded, `LinkedWaiting` before. */
export function LinkedShell({
  q,
  title,
  back,
  children,
  footer,
}: {
  q: UseLinked;
  /** the title bar's words, from their name once it is known */
  title: (name: string | null) => string;
  back: Back;
  children: (linked: Linked) => ReactNode;
  /** a docked ActionBar, after the column */
  footer?: (linked: Linked) => ReactNode;
}) {
  const linked = q.linked;
  return (
    <IonPage>
      {linked ? (
        <IonContent>
          <Screen>
            <TopBar title={title(linked.name)} back={back} />
            <div className="you-page">{children(linked)}</div>
            {footer ? footer(linked) : null}
          </Screen>
        </IonContent>
      ) : (
        <LinkedWaiting q={q} title={title(null)} back={back} />
      )}
    </IonPage>
  );
}

export const familyPath = (id: string, sub = "") => `/you/family/${id}${sub ? `/${sub}` : ""}`;

/** The reader's own name, as "set by …" carries it to the parent's phone (ADR-056). */
export function useMyName(): string {
  const own = useKaya((s) => s.S.profile.name);
  const user = useKaya((s) => s.user);
  return own?.trim() || user?.name?.trim() || user?.email || "Family";
}

/** "set by you" when the reader set it, "set by Rahul" when someone else linked did. */
export function setByWords(setBy: string | null | undefined, me: string): string | null {
  if (!setBy) return null;
  return setBy === me ? "set by you" : `set by ${setBy}`;
}
